(() => {
  'use strict';

  const TEST_HOSTS = new Set(['', 'localhost', '127.0.0.1', '::1']);
  const CCT_COLORS = {
    warm: '255, 198, 122',
    neutral: '255, 255, 255',
    cool: '170, 213, 255'
  };
  const guideMark = (text, tone) => ({ text, tone });
  const GUIDE_COPY = {
    supply: [
      'Use the ', guideMark('red', 'danger'), ' ', guideMark('ON/OFF', 'action'),
      ' control at the upper right of the mirror to disconnect or reconnect simulated ',
      guideMark('external power', 'state'),
      '. It is not a switch on the product.'
    ],
    power: [
      'Tap ', guideMark('Power', 'power'), ' once. ', guideMark('LED ON / backlight OFF', 'state'),
      '; ', guideMark('LED OFF / backlight ON', 'state'), '.'
    ],
    colour: [
      'After tapping ', guideMark('Power', 'power'), ', tap again ',
      guideMark('within 3 seconds', 'timing'), ' of the previous tap for the ',
      guideMark('colour cycle', 'state'), '. After ', guideMark('more than 3 seconds', 'timing'),
      ', the next tap becomes ', guideMark('ON/OFF', 'state'), '.'
    ],
    brightness: [
      'With ', guideMark('LED ON', 'state'), ', ', guideMark('hold Power for 1 second', 'power'),
      '. ', guideMark('Release', 'action'), ' at the brightness you want.'
    ],
    defog: [
      guideMark('Tap Defog', 'action'), ' once to switch the ', guideMark('demister ON/OFF', 'state'), '.'
    ],
    radar: [
      guideMark('Move across the mirror', 'action'), ' to simulate motion and switch the ',
      guideMark('LED ON', 'state'), '.'
    ],
    idle: [
      'After motion is triggered, ', guideMark('no motion for 60 seconds', 'timing'),
      ' switches the ', guideMark('LED OFF', 'state'), '.'
    ],
    'sensor-pause': [
      'With ', guideMark('LED OFF', 'state'), ', ', guideMark('hold Power for 1.5 seconds', 'power'),
      ' to enter ', guideMark('SENSOR PAUSED', 'state'), '.'
    ],
    'sensor-disable': [
      'With ', guideMark('LED OFF', 'state'), ', ', guideMark('hold Power 3 times', 'power'),
      ' in sequence to set ', guideMark('SENSOR DISABLED', 'state'), '.'
    ],
    'sensor-restore': [
      'From ', guideMark('SENSOR DISABLED', 'state'), ': ',
      guideMark('Power: hold twice, tap once, hold once', 'power'),
      ' to reach ', guideMark('SENSOR RESTORED', 'state'), '.'
    ]
  };
  const DEFAULT_SNAPSHOT = {
    format: 'tap-mirror-snapshot',
    version: 2,
    product: {
      name: 'Sensor LED Mirror',
      model: 'baseline-001-010'
    },
    timing: {
      bootSeconds: 3,
      tapCycleWindowSeconds: 3,
      brightnessHoldSeconds: 1,
      serviceHoldSeconds: 1.5,
      serviceStepTimeoutSeconds: 6,
      radarIdleSeconds: 60
    },
    light: {
      defaultBrightness: 80,
      minimumBrightness: 10,
      maximumBrightness: 100,
      defaultCct: 'neutral',
      cctCycle: ['off', 'cool', 'neutral', 'warm']
    },
    attribution: {
      brand: 'Oz Bathroom',
      storeUrl: 'https://www.ozbathroom.com.au',
      allowedDomains: [
        'www.ozbathroom.com.au',
        'ozbathroom-au.myshopify.com'
      ]
    }
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const normalizeHost = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./, '');

  function isTestEnvironment() {
    const params = new URLSearchParams(window.location.search);
    return TEST_HOSTS.has(window.location.hostname)
      || window.location.protocol === 'file:'
      || Boolean(window.Shopify?.designMode)
      || params.has('preview_theme_id');
  }

  function createState(snapshot) {
    const light = snapshot.light;
    return {
      supplyOn: true,
      booting: false,
      lightOn: false,
      brightness: light.defaultBrightness,
      cct: light.defaultCct,
      memory: {
        brightness: light.defaultBrightness,
        cct: light.defaultCct
      },
      defogOn: false,
      sensorMode: 'enabled',
      radarMotion: false,
      indicators: {
        sensor: 'white',
        defog: 'white',
        power: 'white'
      }
    };
  }

  function normalizedText(element) {
    return String(element?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function findProductLabel(root, labels) {
    const wrapper = root.closest('.shopify-section') || root;
    const productArea = root.closest('main') || document.querySelector('main') || document.body;
    const candidates = productArea.querySelectorAll(
      'summary, button, a, [role="button"], h2, h3, h4, [class*="accordion-title"], [class*="accordion__title"], [class*="tab-title"]'
    );
    return [...candidates].find((element) => {
      if (wrapper.contains(element)) return false;
      if (!element.getClientRects().length) return false;
      const text = normalizedText(element);
      return labels.some((label) => text === label || text.startsWith(`${label} `));
    }) || null;
  }

  function productBlockFor(element) {
    if (!element) return null;
    return element.closest(
      'details, product-tab, [data-block-id], .shopify-block, .product__accordion, .productView-accordion, .productView-tab, .product-block, .accordion-item, .tab-content'
    ) || element;
  }

  function configureProductPlacement(root) {
    const mobileEnabled = root.dataset.mobileProductPlacement === 'true';
    const desktopEnabled = root.dataset.desktopProductLayout === 'true';
    if ((!mobileEnabled && !desktopEnabled) || window.location.protocol === 'file:') return;

    const wrapper = root.closest('.shopify-section') || root;
    const originalParent = wrapper.parentNode;
    if (!originalParent) return;

    const wrapperPlaceholder = document.createComment('LED mirror simulator original position');
    originalParent.insertBefore(wrapperPlaceholder, wrapper);
    const mobileMedia = window.matchMedia('(max-width: 767px)');
    const desktopMedia = window.matchMedia('(min-width: 990px)');
    let observer = null;
    let stopTimer = null;
    let desktopLayout = null;
    let descriptionBlock = null;
    let shippingBlock = null;
    let descriptionPlaceholder = null;
    let shippingPlaceholder = null;

    const restoreWrapper = () => {
      if (wrapperPlaceholder.parentNode && wrapper.previousSibling !== wrapperPlaceholder) {
        wrapperPlaceholder.parentNode.insertBefore(wrapper, wrapperPlaceholder.nextSibling);
      }
      root.classList.remove('is-mobile-relocated');
      root.classList.remove('is-desktop-relocated');
    };

    const restoreDesktopBlocks = () => {
      if (descriptionPlaceholder?.parentNode && descriptionBlock) {
        descriptionPlaceholder.parentNode.insertBefore(descriptionBlock, descriptionPlaceholder.nextSibling);
      }
      if (shippingPlaceholder?.parentNode && shippingBlock) {
        shippingPlaceholder.parentNode.insertBefore(shippingBlock, shippingPlaceholder.nextSibling);
      }
      desktopLayout?.remove();
      descriptionPlaceholder?.remove();
      shippingPlaceholder?.remove();
      desktopLayout = null;
      descriptionBlock = null;
      shippingBlock = null;
      descriptionPlaceholder = null;
      shippingPlaceholder = null;
      root.classList.remove('is-desktop-relocated');
    };

    const restoreAll = () => {
      restoreDesktopBlocks();
      restoreWrapper();
    };

    const placeMobile = () => {
      restoreDesktopBlocks();
      restoreWrapper();
      const description = productBlockFor(findProductLabel(root, ['description']));
      if (description?.parentNode && !description.contains(wrapper)) {
        description.parentNode.insertBefore(wrapper, description);
        root.classList.add('is-mobile-relocated');
        return true;
      }

      const enquire = productBlockFor(findProductLabel(root, ['enquire now', 'inquire now']));
      if (enquire?.parentNode && !enquire.contains(wrapper)) {
        enquire.parentNode.insertBefore(wrapper, enquire.nextSibling);
        root.classList.add('is-mobile-relocated');
        return true;
      }
      return false;
    };

    const placeDesktop = () => {
      if (desktopLayout?.isConnected) return true;
      restoreAll();

      descriptionBlock = productBlockFor(findProductLabel(root, ['description']));
      shippingBlock = productBlockFor(findProductLabel(root, [
        'shipping & return',
        'shipping and return',
        'shipping & returns',
        'shipping and returns'
      ]));
      if (!descriptionBlock?.parentNode
        || !shippingBlock?.parentNode
        || descriptionBlock.contains(wrapper)
        || shippingBlock.contains(wrapper)) {
        descriptionBlock = null;
        shippingBlock = null;
        return false;
      }

      const layoutParent = descriptionBlock.parentNode;
      descriptionPlaceholder = document.createComment('Description original position');
      layoutParent.insertBefore(descriptionPlaceholder, descriptionBlock);
      if (shippingBlock !== descriptionBlock) {
        shippingPlaceholder = document.createComment('Shipping and return original position');
        shippingBlock.parentNode.insertBefore(shippingPlaceholder, shippingBlock);
      }

      desktopLayout = document.createElement('div');
      desktopLayout.className = 'led-mirror-snapshot-product-layout';
      const detailsColumn = document.createElement('div');
      detailsColumn.className = 'led-mirror-snapshot-product-layout__details';
      const simulatorColumn = document.createElement('div');
      simulatorColumn.className = 'led-mirror-snapshot-product-layout__simulator';
      desktopLayout.append(detailsColumn, simulatorColumn);
      layoutParent.insertBefore(desktopLayout, descriptionPlaceholder);
      detailsColumn.append(descriptionBlock);
      if (shippingBlock !== descriptionBlock) detailsColumn.append(shippingBlock);
      simulatorColumn.append(wrapper);
      root.classList.add('is-desktop-relocated');
      return true;
    };

    const place = () => {
      if (mobileEnabled && mobileMedia.matches) return placeMobile();
      if (desktopEnabled && desktopMedia.matches) return placeDesktop();
      restoreAll();
      return true;
    };

    const update = () => {
      if (place() || observer) return;
      observer = new MutationObserver(() => {
        if (!place()) return;
        observer.disconnect();
        observer = null;
      });
      observer.observe(document.body, { childList: true, subtree: true });
      stopTimer = window.setTimeout(() => {
        observer?.disconnect();
        observer = null;
      }, 10000);
    };

    mobileMedia.addEventListener?.('change', update);
    desktopMedia.addEventListener?.('change', update);
    update();

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target?.contains(root)) return;
      mobileMedia.removeEventListener?.('change', update);
      desktopMedia.removeEventListener?.('change', update);
      observer?.disconnect();
      if (stopTimer) window.clearTimeout(stopTimer);
      restoreAll();
      wrapperPlaceholder.remove();
    });
  }

  class EngagementTracker {
    constructor(root, snapshot) {
      this.root = root;
      this.snapshot = snapshot;
      this.endpoint = String(root.dataset.analyticsEndpoint || '').trim();
      this.anonKey = String(root.dataset.supabaseAnonKey || '').trim();
      this.sessionId = crypto.randomUUID?.() || `mirror-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.startedAt = null;
      this.lastActiveAt = null;
      this.activeMilliseconds = 0;
      this.lastFlushAt = 0;
      this.flushTimer = null;
      this.counters = {
        supply_toggles: 0,
        power_taps: 0,
        defog_taps: 0,
        brightness_adjustments: 0,
        colour_changes: 0,
        sensor_triggers: 0,
        service_actions: 0
      };
      this.feedbackForm = root.querySelector('[data-feedback-form]');
      this.feedbackStatus = root.querySelector('[data-feedback-status]');
      this.bind();
    }

    analyticsAllowed() {
      try {
        const privacy = window.Shopify?.customerPrivacy;
        return !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
      } catch {
        return false;
      }
    }

    bind() {
      this.feedbackForm?.addEventListener('submit', (event) => this.submitFeedback(event));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
      window.addEventListener('pagehide', () => this.flush(true));
    }

    track(name, amount = 1) {
      if (!this.endpoint || !this.anonKey || !this.analyticsAllowed()) return;
      const now = Date.now();
      if (!this.startedAt) {
        this.startedAt = now;
        this.lastActiveAt = now;
        this.lastFlushAt = now;
        this.scheduleFlush();
      } else if (this.lastActiveAt) {
        this.activeMilliseconds += Math.min(now - this.lastActiveAt, 15000);
        this.lastActiveAt = now;
      }
      if (Object.prototype.hasOwnProperty.call(this.counters, name)) {
        this.counters[name] += amount;
      }
      if (now - this.lastFlushAt >= 30000) this.flush(false);
    }

    scheduleFlush() {
      if (this.flushTimer) return;
      this.flushTimer = window.setInterval(() => this.flush(false), 30000);
    }

    payload(action = 'session') {
      const now = Date.now();
      const liveMilliseconds = this.lastActiveAt ? Math.min(now - this.lastActiveAt, 15000) : 0;
      const product = this.snapshot.product || {};
      return {
        action,
        session_id: this.sessionId,
        section_id: this.root.dataset.sectionId || '',
        product_id: this.root.dataset.productId || '',
        product_handle: this.root.dataset.productHandle || '',
        product_title: this.root.dataset.productTitle || product.name || '',
        product_model: product.model || '',
        page_url: window.location.href.slice(0, 1000),
        referrer: document.referrer.slice(0, 1000),
        started_at: this.startedAt ? new Date(this.startedAt).toISOString() : new Date().toISOString(),
        last_seen_at: new Date().toISOString(),
        active_seconds: Math.max(1, Math.round((this.activeMilliseconds + liveMilliseconds) / 1000)),
        counters: { ...this.counters },
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      };
    }

    async request(payload, keepalive = false) {
      if (!this.endpoint || !this.anonKey) return null;
      const response = await fetch(this.endpoint, {
        method: 'POST',
        keepalive,
        headers: {
          'Content-Type': 'application/json',
          apikey: this.anonKey,
          Authorization: `Bearer ${this.anonKey}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Request failed: ${response.status}`);
      return data;
    }

    async flush(keepalive) {
      if (!this.startedAt || !this.analyticsAllowed()) return;
      const now = Date.now();
      this.lastFlushAt = now;
      try {
        await this.request(this.payload(), keepalive);
      } catch (error) {
        console.warn('[LED mirror v2] analytics upload failed', error);
      }
    }

    async submitFeedback(event) {
      event.preventDefault();
      if (!this.feedbackForm?.reportValidity()) return;
      if (!this.endpoint || !this.anonKey) {
        this.feedbackStatus.textContent = 'Feedback is not configured yet. Please contact the store directly.';
        this.feedbackStatus.dataset.state = 'error';
        return;
      }
      const submit = this.feedbackForm.querySelector('[type="submit"]');
      const form = new FormData(this.feedbackForm);
      const feedback = String(form.get('feedback') || '').trim();
      const email = String(form.get('email') || '').trim();
      const consent = form.get('consent') === 'on';
      if (!feedback || !email || !consent) return;

      submit.disabled = true;
      this.feedbackStatus.textContent = 'Sending your feedback...';
      this.feedbackStatus.dataset.state = 'pending';
      try {
        const result = await this.request({
          ...this.payload('feedback'),
          feedback,
          email,
          consent: true
        });
        this.feedbackForm.reset();
        this.feedbackStatus.textContent = result?.message || 'Thank you for joining in. We will review your feedback and email you if it qualifies for a per-mirror discount.';
        this.feedbackStatus.dataset.state = 'success';
      } catch (error) {
        const message = String(error?.message || '');
        this.feedbackStatus.textContent = /requested function was not found|request failed: 404/i.test(message)
          ? 'Feedback service is not deployed yet. Please contact the store team.'
          : `We could not send your feedback: ${message || 'please try again.'}`;
        this.feedbackStatus.dataset.state = 'error';
        console.warn('[LED mirror v2] feedback failed', error);
      } finally {
        submit.disabled = false;
      }
    }

    destroy() {
      if (this.flushTimer) window.clearInterval(this.flushTimer);
      this.flushTimer = null;
      this.flush(true);
    }
  }

  class MirrorSnapshot {
    constructor(root, snapshot) {
      this.root = root;
      this.snapshot = snapshot;
      this.state = createState(snapshot);
      this.timers = new Set();
      this.radarIdleTimer = null;
      this.radarVisualTimer = null;
      this.press = null;
      this.lastPowerTapAt = null;
      this.tapCycleIndex = 0;
      this.rampTimer = null;
      this.rampDirection = 1;
      this.service = { mode: null, step: 0, timeout: null, pulse: null };
      this.interactionCount = 0;
      this.tracker = new EngagementTracker(root, snapshot);

      this.els = {
        stage: root.querySelector('[data-stage]'),
        mirrorWrap: root.querySelector('[data-mirror-wrap]'),
        mirror: root.querySelector('[data-mirror]'),
        heater: root.querySelector('.led-mirror-snapshot__heater'),
        sensorPulse: root.querySelector('.led-mirror-snapshot__sensor-pulse'),
        readout: root.querySelector('[data-readout]'),
        status: root.querySelector('[data-status]'),
        supply: root.querySelector('[data-supply]'),
        sensorMini: root.querySelector('[data-indicator-mini="sensor"]'),
        defogMini: root.querySelector('[data-indicator-mini="defog"]'),
        powerMini: root.querySelector('[data-indicator-mini="power"]'),
        sensorBig: root.querySelector('[data-indicator-big="sensor"]'),
        defogBig: root.querySelector('[data-indicator-big="defog"]'),
        powerBig: root.querySelector('[data-indicator-big="power"]'),
        sensorTimer: root.querySelector('[data-sensor-timer]'),
        defogButton: root.querySelector('[data-control="defog"]'),
        powerButton: root.querySelector('[data-control="power"]'),
        guideSelect: root.querySelector('[data-guide-select]'),
        guideText: root.querySelector('[data-guide-text]'),
        interactionCount: root.querySelector('[data-interaction-count]'),
        attribution: root.querySelector('[data-attribution]'),
        attributionBrand: root.querySelector('[data-attribution-brand]'),
        attributionLink: root.querySelector('[data-attribution-link]')
      };

      this.bind();
      this.updateGuide();
      this.configureAttribution();
      this.render();
      this.scheduleRadarIdle();
      this.startClock();
    }

    delay(callback, milliseconds) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        callback();
      }, milliseconds);
      this.timers.add(timer);
      return timer;
    }

    bind() {
      this.els.supply?.addEventListener('click', () => this.toggleSupply());
      this.bindPress(this.els.powerButton, 'power');
      this.bindPress(this.els.defogButton, 'defog');

      const radarInput = (event) => this.detectRadar(event);
      this.els.mirror?.addEventListener('pointermove', radarInput);
      this.els.mirror?.addEventListener('pointerenter', radarInput);
      this.els.mirror?.addEventListener('pointerleave', () => this.clearRadarVisual());
      this.els.mirror?.addEventListener('pointercancel', () => this.clearRadarVisual());
      this.els.mirror?.addEventListener('touchmove', radarInput, { passive: true });
      this.els.mirror?.addEventListener('touchend', () => this.clearRadarVisual(), { passive: true });
      this.els.guideSelect?.addEventListener('change', () => this.updateGuide());

      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target?.contains(this.root)) this.destroy();
      });
    }

    updateGuide() {
      if (!this.els.guideText) return;
      const selectedGuide = this.els.guideSelect?.value || 'supply';
      const parts = GUIDE_COPY[selectedGuide] || GUIDE_COPY.supply;
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (typeof part === 'string') {
          fragment.append(document.createTextNode(part));
          return;
        }
        const highlight = document.createElement('strong');
        highlight.className = `led-mirror-snapshot__guide-keyword is-${part.tone}`;
        highlight.textContent = part.text;
        fragment.append(highlight);
      });
      this.els.guideText.replaceChildren(fragment);
    }

    recordInteraction() {
      this.interactionCount += 1;
      if (this.els.interactionCount) {
        this.els.interactionCount.textContent = String(this.interactionCount).padStart(2, '0');
      }
    }

    bindPress(element, control) {
      if (!element) return;

      const start = (event) => {
        if (!this.state.supplyOn || this.press) return;
        event.preventDefault();
        element.setPointerCapture?.(event.pointerId);
        this.press = {
          control,
          startedAt: performance.now(),
          consumed: false,
          brightnessStarted: false,
          timers: []
        };

        if (control === 'power') {
          this.press.timers.push(this.delay(() => {
            if (!this.press || this.press.control !== 'power' || !this.state.lightOn) return;
            this.press.consumed = true;
            this.press.brightnessStarted = true;
            this.startBrightnessRamp();
          }, this.snapshot.timing.brightnessHoldSeconds * 1000));

          this.press.timers.push(this.delay(() => {
            if (!this.press || this.press.control !== 'power' || this.state.lightOn) return;
            this.press.consumed = true;
            this.handleServiceHold();
          }, this.snapshot.timing.serviceHoldSeconds * 1000));
        }
      };

      const end = (event) => {
        if (!this.press || this.press.control !== control) return;
        event.preventDefault();
        const activePress = this.press;
        this.press = null;
        activePress.timers.forEach((timer) => {
          window.clearTimeout(timer);
          this.timers.delete(timer);
        });

        if (activePress.brightnessStarted) {
          this.stopBrightnessRamp(true);
          return;
        }
        if (activePress.consumed) return;
        if (control === 'power') this.tapPower();
        if (control === 'defog') this.tapDefog();
      };

      element.addEventListener('pointerdown', start);
      element.addEventListener('pointerup', end);
      element.addEventListener('pointercancel', end);
      element.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        control === 'power' ? this.tapPower() : this.tapDefog();
      });
    }

    runBoot() {
      if (!this.state.supplyOn) return;
      this.clearService();
      this.state.booting = true;
      this.state.lightOn = true;
      this.state.brightness = this.snapshot.light.maximumBrightness;
      this.state.cct = 'neutral';
      this.state.defogOn = false;
      this.state.radarMotion = false;
      this.state.indicators.power = 'white';
      this.state.indicators.defog = 'white';
      this.state.indicators.sensor = this.state.sensorMode === 'permanent' ? 'off' : 'white';
      this.render();

      this.delay(() => {
        if (!this.state.supplyOn) return;
        this.state.booting = false;
        this.state.lightOn = false;
        this.state.brightness = this.state.memory.brightness;
        this.state.cct = this.state.memory.cct;
        this.state.indicators.power = 'white';
        this.state.indicators.defog = 'white';
        this.state.indicators.sensor = this.state.sensorMode === 'permanent' ? 'off' : 'white';
        this.render();
        this.scheduleRadarIdle();
      }, this.snapshot.timing.bootSeconds * 1000);
    }

    toggleSupply() {
      this.recordInteraction();
      this.tracker.track('supply_toggles');
      this.state.supplyOn = !this.state.supplyOn;
      this.clearRuntimeTimers();

      if (!this.state.supplyOn) {
        this.state.booting = false;
        this.state.lightOn = false;
        this.state.defogOn = false;
        this.state.radarMotion = false;
        this.state.indicators = { sensor: 'off', defog: 'off', power: 'off' };
        this.render();
        return;
      }

      this.runBoot();
    }

    tapPower() {
      if (!this.state.supplyOn || this.state.booting) return;
      this.recordInteraction();
      this.tracker.track('power_taps');
      if (this.handleServiceTap()) return;

      const now = performance.now();
      const inCycle = this.lastPowerTapAt !== null
        && now - this.lastPowerTapAt <= this.snapshot.timing.tapCycleWindowSeconds * 1000;
      this.lastPowerTapAt = now;

      if (!inCycle) {
        this.tapCycleIndex = 0;
        if (this.state.sensorMode === 'temporary') {
          this.state.sensorMode = 'enabled';
          this.state.indicators.sensor = 'white';
        }
        if (this.state.lightOn) {
          this.state.lightOn = false;
          this.state.indicators.power = 'white';
        } else {
          this.readMemory();
          this.state.lightOn = true;
          this.state.indicators.power = 'off';
        }
        this.render();
        return;
      }

      const cycle = this.snapshot.light.cctCycle;
      this.tapCycleIndex = (this.tapCycleIndex + 1) % cycle.length;
      const selection = cycle[this.tapCycleIndex];
      this.tracker.track('colour_changes');
      if (this.state.sensorMode === 'temporary') {
        this.state.sensorMode = 'enabled';
        this.state.indicators.sensor = 'white';
      }
      if (selection === 'off') {
        this.state.lightOn = false;
        this.state.indicators.power = 'white';
      } else {
        this.state.lightOn = true;
        this.state.cct = selection;
        this.state.indicators.power = 'off';
        this.writeMemory();
      }
      this.render();
    }

    tapDefog() {
      if (!this.state.supplyOn || this.state.booting || this.service.mode) return;
      this.recordInteraction();
      this.tracker.track('defog_taps');
      this.state.defogOn = !this.state.defogOn;
      this.state.indicators.defog = this.state.defogOn ? 'off' : 'white';
      this.render();
    }

    startBrightnessRamp() {
      this.recordInteraction();
      this.stopBrightnessRamp(false);
      const min = this.snapshot.light.minimumBrightness;
      const max = this.snapshot.light.maximumBrightness;
      const step = this.rampDirection > 0 ? 2.5 : -2.5;

      const update = () => {
        this.state.brightness = clamp(this.state.brightness + step, min, max);
        this.render();
      };
      update();
      this.rampTimer = window.setInterval(update, 90);
    }

    stopBrightnessRamp(commit) {
      if (this.rampTimer) window.clearInterval(this.rampTimer);
      this.rampTimer = null;
      if (!commit) return;
      this.tracker.track('brightness_adjustments');
      this.writeMemory();
      this.rampDirection *= -1;
      this.render();
    }

    writeMemory() {
      this.state.memory.brightness = this.state.brightness;
      this.state.memory.cct = this.state.cct;
    }

    readMemory() {
      this.state.brightness = this.state.memory.brightness;
      this.state.cct = this.state.memory.cct;
    }

    detectRadar() {
      if (!this.state.supplyOn
        || this.state.booting
        || this.state.sensorMode !== 'enabled'
        || this.service.mode) return;

      if (!this.state.radarMotion) {
        this.recordInteraction();
        this.tracker.track('sensor_triggers');
      }

      this.state.radarMotion = true;
      if (!this.state.lightOn) {
        this.readMemory();
        this.state.lightOn = true;
        this.state.indicators.power = 'off';
      }
      this.render();

      if (this.radarVisualTimer) window.clearTimeout(this.radarVisualTimer);
      this.radarVisualTimer = this.delay(() => {
        this.radarVisualTimer = null;
        this.state.radarMotion = false;
        this.render();
      }, 650);
      this.scheduleRadarIdle();
    }

    clearRadarVisual() {
      if (this.radarVisualTimer) {
        window.clearTimeout(this.radarVisualTimer);
        this.timers.delete(this.radarVisualTimer);
      }
      this.radarVisualTimer = null;
      if (!this.state.radarMotion) return;
      this.state.radarMotion = false;
      this.render();
    }

    scheduleRadarIdle() {
      if (this.radarIdleTimer) window.clearTimeout(this.radarIdleTimer);
      if (this.state.sensorMode !== 'enabled' || !this.state.supplyOn) return;
      this.radarIdleStartedAt = performance.now();
      this.radarIdleTimer = this.delay(() => {
        this.radarIdleTimer = null;
        if (this.state.sensorMode !== 'enabled' || this.state.radarMotion) return;
        this.state.lightOn = false;
        this.state.indicators.power = 'white';
        this.render();
      }, this.snapshot.timing.radarIdleSeconds * 1000);
    }

    handleServiceHold() {
      if (this.state.lightOn || !this.state.supplyOn) return;
      this.recordInteraction();
      this.tracker.track('service_actions');

      if (!this.service.mode) {
        this.service.mode = this.state.sensorMode === 'permanent' ? 'restore' : 'disable';
        this.service.step = 1;
        this.state.sensorMode = this.service.mode === 'restore' ? 'permanent' : 'temporary';
        this.state.radarMotion = false;
        this.state.indicators.sensor = 'off';
        this.state.indicators.power = 'off';
        this.scheduleServiceTimeout();
        this.render();
        return;
      }

      if (this.service.step === 1) {
        this.service.step = 2;
        this.state.indicators.power = 'blueFlash';
        this.scheduleServiceTimeout();
        this.render();
        return;
      }

      if (this.service.mode === 'disable' && this.service.step === 2) {
        this.state.sensorMode = 'permanent';
        this.state.indicators.sensor = 'off';
        this.state.indicators.power = 'white';
        this.clearService();
        this.render();
        return;
      }

      if (this.service.mode === 'restore' && this.service.step === 3) {
        this.state.sensorMode = 'temporary';
        this.state.indicators.sensor = 'off';
        this.state.indicators.power = 'white';
        this.clearService();
        this.render();
      }
    }

    handleServiceTap() {
      if (!this.service.mode) return false;
      if (this.service.step === 1) {
        this.clearService();
        return false;
      }
      if (this.service.mode === 'restore' && this.service.step === 2) {
        this.service.step = 3;
        this.state.indicators.power = 'white';
        this.render();
        this.service.pulse = this.delay(() => {
          if (this.service.mode === 'restore' && this.service.step === 3) {
            this.state.indicators.power = 'blueFlash';
            this.render();
          }
        }, 160);
        this.scheduleServiceTimeout();
      }
      return true;
    }

    scheduleServiceTimeout() {
      if (this.service.timeout) window.clearTimeout(this.service.timeout);
      this.service.timeout = this.delay(() => {
        if (this.service.mode === 'disable') {
          this.state.sensorMode = 'temporary';
          this.state.indicators.sensor = 'off';
          this.state.indicators.power = 'off';
        } else if (this.service.mode === 'restore') {
          this.state.sensorMode = 'permanent';
          this.state.indicators.sensor = 'off';
          this.state.indicators.power = 'white';
        }
        this.clearService();
        this.render();
      }, this.snapshot.timing.serviceStepTimeoutSeconds * 1000);
    }

    clearService() {
      if (this.service.timeout) window.clearTimeout(this.service.timeout);
      if (this.service.pulse) window.clearTimeout(this.service.pulse);
      this.service = { mode: null, step: 0, timeout: null, pulse: null };
    }

    clearRuntimeTimers() {
      this.stopBrightnessRamp(false);
      this.clearService();
      if (this.radarIdleTimer) window.clearTimeout(this.radarIdleTimer);
      if (this.radarVisualTimer) window.clearTimeout(this.radarVisualTimer);
      this.radarIdleTimer = null;
      this.radarVisualTimer = null;
    }

    configureAttribution() {
      const attribution = this.snapshot.attribution || {};
      const dataDomains = this.root.dataset.officialDomains || '';
      const allowed = [
        ...(attribution.allowedDomains || []),
        ...dataDomains.split(',')
      ].map(normalizeHost).filter(Boolean);
      const host = normalizeHost(window.location.hostname);
      const official = allowed.some((domain) => host === domain || host.endsWith(`.${domain}`));
      const test = isTestEnvironment();

      if (official || test || !this.els.attribution) return;

      const storeUrl = this.root.dataset.officialStoreUrl || attribution.storeUrl || '#';
      const brand = this.root.dataset.brand || attribution.brand || 'Official store';
      this.els.attributionBrand.textContent = brand;
      this.els.attributionLink.href = storeUrl;
      this.els.attribution.hidden = false;
    }

    statusText() {
      if (!this.state.supplyOn) return 'POWER OFF';
      if (this.state.booting) return 'INITIALIZING';
      if (this.state.sensorMode === 'permanent') return 'SENSOR OFF';
      if (this.state.sensorMode === 'temporary') return 'SENSOR PAUSED';
      if (this.state.radarMotion) return 'MOTION';
      return 'READY';
    }

    renderIndicator(name, mode) {
      const color = mode.startsWith('blue') ? '#54a7ff' : '#ffffff';
      const on = mode !== 'off' && this.state.supplyOn;
      const flash = mode.endsWith('Flash');
      [this.els[`${name}Mini`], this.els[`${name}Big`]].forEach((element) => {
        if (!element) return;
        element.classList.toggle('is-on', on);
        element.classList.toggle('is-flashing', on && flash);
        element.style.setProperty('--indicator-color', color);
      });
    }

    render() {
      const state = this.state;
      this.root.style.setProperty('--mirror-cct', CCT_COLORS[state.cct] || CCT_COLORS.neutral);
      this.root.style.setProperty('--mirror-strength', String(Math.max(0.12, state.brightness / 100)));
      this.root.style.setProperty('--mirror-glow', String(Math.max(0.18, state.brightness / 100)));
      this.root.classList.toggle('is-light-on', state.supplyOn && state.lightOn);
      this.root.classList.toggle('is-power-off', !state.supplyOn);
      this.root.classList.toggle('is-defog-on', state.supplyOn && state.defogOn);
      this.root.classList.toggle('is-radar-active', state.radarMotion);
      if (this.els.heater) {
        this.els.heater.classList.toggle('is-active', state.supplyOn && state.defogOn);
        this.els.heater.style.display = 'block';
        this.els.heater.style.opacity = state.supplyOn && state.defogOn ? '1' : '0';
      }
      if (this.els.sensorPulse) {
        this.els.sensorPulse.classList.toggle('is-active', state.radarMotion);
        this.els.sensorPulse.style.display = 'block';
        this.els.sensorPulse.style.visibility = 'visible';
      }
      this.els.supply?.classList.toggle('is-off', !state.supplyOn);
      this.els.supply?.setAttribute('aria-pressed', state.supplyOn ? 'true' : 'false');
      this.els.status.textContent = this.statusText();
      this.els.readout.textContent = state.lightOn
        ? `${Math.round(state.brightness)}% / ${state.cct.toUpperCase()}`
        : 'LED OFF';

      this.renderIndicator('sensor', state.indicators.sensor);
      this.renderIndicator('defog', state.indicators.defog);
      this.renderIndicator('power', state.indicators.power);
    }

    startClock() {
      const tick = () => {
        if (!this.root.isConnected) return;
        if (this.els.sensorTimer) {
          const elapsed = this.radarIdleStartedAt
            ? Math.floor((performance.now() - this.radarIdleStartedAt) / 1000)
            : 0;
          this.els.sensorTimer.textContent = String(clamp(elapsed, 0, 59)).padStart(2, '0');
        }
        window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    }

    destroy() {
      this.tracker.destroy();
      this.clearRuntimeTimers();
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers.clear();
    }
  }

  async function loadSnapshot(root) {
    const url = root.dataset.snapshotUrl;
    if (!url) return DEFAULT_SNAPSHOT;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Snapshot request failed: ${response.status}`);
    return {
      ...DEFAULT_SNAPSHOT,
      ...await response.json()
    };
  }

  async function initialize(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    configureProductPlacement(root);
    try {
      const snapshot = await loadSnapshot(root);
      new MirrorSnapshot(root, snapshot);
    } catch (error) {
      console.warn('[LED mirror snapshot] using built-in snapshot fallback', error);
      new MirrorSnapshot(root, DEFAULT_SNAPSHOT);
    }
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-led-mirror-snapshot]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
